import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { UseMutationResult } from "@tanstack/react-query";
import { CollapsePanel } from "../motion";
import type { RotaImportResult } from "../hooks/useRota";

export function RotaImportWizard({
  dryRun, commit, onDone,
}: {
  dryRun: UseMutationResult<RotaImportResult, unknown, File, unknown>;
  commit: UseMutationResult<RotaImportResult, unknown, File, unknown>;
  onDone: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<RotaImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function preview() {
    setError(null);
    setResult(null);
    if (!file) return;
    dryRun.mutate(file, { onSuccess: setResult, onError: (e) => setError(e instanceof Error ? e.message : "Could not read the PDF") });
  }

  function confirm() {
    if (!file) return;
    commit.mutate(file, {
      onSuccess: (r) => { setResult(r); onDone(); },
      onError: (e) => setError(e instanceof Error ? e.message : "Import failed"),
    });
  }

  const p = result?.preview;

  return (
    <CollapsePanel>
      <div className="form-card">
      <div className="file-picker">
        <button type="button" className="btn btn-secondary file-picker-btn" onClick={() => fileInputRef.current?.click()}>
          {file ? "Change file" : "Choose PDF"}
        </button>
        <span className="file-picker-name">{file ? file.name : "No file chosen"}</span>
        <input id="rota-file" ref={fileInputRef} tabIndex={-1} type="file" accept="application/pdf" className="sr-only" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }} />
      </div>
          <div className="form-actions">
        <button type="button" className="btn btn-primary" disabled={!file || dryRun.isPending} onClick={preview}>
          {dryRun.isPending ? "Reading…" : "Preview"}
        </button>
        {p && !result?.commit ? (
          <button type="button" className="btn btn-secondary" disabled={commit.isPending} onClick={confirm}>
            {commit.isPending ? "Importing…" : "Confirm import"}
          </button>
        ) : null}
      </div>
      {error ? <p className="error">{error}</p> : null}

      {result?.commit ? (
        <p role="status" style={{ margin: 0 }}>
          <strong>Imported {result.summary.fileName}.</strong> +{result.summary.added} added, {result.summary.changed} changed, {result.summary.removed} removed.
        </p>
      ) : null}

      {p && !result?.commit ? (
        <div>
          {p.unreadDays.length > 0 ? (
            <p className="error" role="alert">
              ⚠️ {p.unreadDays.length} day(s) could not be read ({p.unreadDays.join(", ")}). Add those shifts manually after importing.
            </p>
          ) : null}
          {result?.summary.unknownHomes.length ? (
            <p className="error" role="alert">⚠️ Unknown home(s) skipped: {result.summary.unknownHomes.join(", ")}</p>
          ) : null}
          {p.skippedManual.length > 0 ? <p className="muted">ℹ️ {p.skippedManual.length} PDF row(s) kept as manual (manual edits are never overwritten).</p> : null}

          {p.added.length > 0 ? (
            <>
              <h3 className="section-title">Add ({p.added.length})</h3>
              <ul className="todo-list">
                {p.added.map((a) => (
                  <li key={a.date} className="todo-row"><span className="chip">{a.date}</span><span>{a.homeName}</span><span className="chip">{a.to?.startTime}–{a.to?.endTime}</span></li>
                ))}
              </ul>
            </>
          ) : null}
          {p.changed.length > 0 ? (
            <>
              <h3 className="section-title">Change ({p.changed.length})</h3>
              <ul className="todo-list">
                {p.changed.map((c) => (
                  <li key={c.date} className="todo-row"><span className="chip">{c.date}</span><span>{c.homeName}</span><span className="muted">{c.from?.startTime}–{c.from?.endTime} → {c.to?.startTime}–{c.to?.endTime}</span></li>
                ))}
              </ul>
            </>
          ) : null}
          {p.removed.length > 0 ? (
            <>
              <h3 className="section-title">Remove ({p.removed.length})</h3>
              <ul className="todo-list">
                {p.removed.map((r) => (
                  <li key={r.date} className="todo-row"><span className="chip">{r.date}</span><span>{r.homeName}</span><span className="chip chip-danger">removed</span></li>
                ))}
              </ul>
            </>
          ) : null}
          {p.unchanged.length > 0 ? <p className="muted">ℹ️ {p.unchanged.length} unchanged.</p> : null}
          <p className="muted">Review the diff, then press “Confirm import”. <Link to="/log">Open a day’s log</Link> after importing.</p>
        </div>
      ) : null}
      </div>
    </CollapsePanel>
  );
}
